import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { fetchUsersByTypes } from '../../../api/collaboratorsApi';
import {
  fetchCarAudit,
  fetchCarAuditFeedback,
  searchAuditCars,
} from '../../../api/auditApi';
import { AuditFilters } from '../../../interfaces/Audit';
import { queryKeys } from '../queryKeys';

export function useAuditCarsSearch(query: string, page: number = 1, limit: number = 10) {
  return useQuery({
    queryKey: queryKeys.audit.carsSearch({ query, page, limit }),
    queryFn: () => searchAuditCars(query, page, limit),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export const CAR_AUDIT_PAGE_SIZE = 50;

// Timeline paginada (at-28): cada página vem do backend com { totalEvents, page,
// limit, events }; o "Carregar mais" da tela acumula as páginas via fetchNextPage.
/**
 * at-90: `soFeedback` troca a ROTA consultada (não um filtro) — a aba Feedback tem endpoint
 * próprio, com curadoria e resumo reduzido feitos no servidor.
 *
 * ⚠️ O modo entra na `queryKey`: sem isso, as duas abas compartilhariam cache e a aba Veículos
 * poderia exibir a timeline curada (sem checklist, sem horas) achando que é a completa.
 */
export function useCarAuditInfinite(carId: string, filters: AuditFilters, soFeedback = false) {
  return useInfiniteQuery({
    queryKey: queryKeys.audit.carDetail({
      carId,
      ...filters,
      limit: CAR_AUDIT_PAGE_SIZE,
      ...(soFeedback ? { modo: 'feedback' } : {}),
    }),
    queryFn: ({ pageParam }) =>
      (soFeedback ? fetchCarAuditFeedback : fetchCarAudit)(carId, {
        ...filters,
        page: pageParam as number,
        limit: CAR_AUDIT_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const page = lastPage.page ?? 1;
      const limit = lastPage.limit ?? CAR_AUDIT_PAGE_SIZE;
      return page * limit < lastPage.totalEvents ? page + 1 : undefined;
    },
    enabled: Boolean(carId),
    staleTime: 30_000,
  });
}

export function useAuditCollaborators() {
  return useQuery({
    queryKey: queryKeys.audit.collaborators,
    queryFn: () => fetchUsersByTypes(['CHEFE_DE_OFICINA', 'COLABORADOR', 'COMPRA', 'ESTOQUE', 'ORCAMENTO', 'RECEPCAO']),
    staleTime: 5 * 60_000,
  });
}
